import React, { useState, useRef, useCallback } from 'react';
import { uploadToRestaurant } from '../../../lib/api/user.apis';

// Type Definitions
interface PhotoUploadProps {
  restaurantId: string;
  authToken: string;
  onUploadComplete?: (results: UploadResult[]) => void;
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;  
}

interface SelectedFile {
  id: string;
  file: File;
  caption: string;
  category: string;
  preview: string;
  isPreviewLoading: boolean;
  errors: string[];
}

interface UploadProgress {
  status: 'uploading' | 'success' | 'error';
  current?: number;
  total?: number;
}

interface UploadResult {
  success: boolean;
  fileId: string;
  fileName: string;
  data?: any;
  error?: string;
}

interface Category {
  value: string;
  label: string;
}

// Constants
const ALLOWED_EXTENSIONS: string[] = [
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.heic',
  '.heif'
];

const EXTENSION_TO_MIME_TYPES: Record<string, string[]> = {
  '.jpg': ['image/jpeg', 'image/jpg'],
  '.jpeg': ['image/jpeg', 'image/jpg'],
  '.png': ['image/png'],
  '.webp': ['image/webp'],
  // iOS/Windows uploads may label HEIC/HEIF differently depending on browser/OS stack.
  '.heic': ['image/heic', 'image/heic-sequence', 'application/octet-stream'],
  '.heif': ['image/heif', 'image/heif-sequence', 'application/octet-stream']
};

const MAX_FILE_SIZE: number = 10 * 1024 * 1024; // 10MB

const CATEGORIES: Category[] = [
  { value: '', label: 'Select category (optional)' },
  { value: 'food', label: 'Food' },
  { value: 'ambiance', label: 'Ambiance' },
  { value: 'menu', label: 'Menu' },
  { value: 'exterior', label: 'Exterior' },
  { value: 'other', label: 'Other' }
];

const HEIC_HEIF_EXTENSIONS = ['.heic', '.heif'];

const getFileExtension = (fileName: string): string | undefined => {
  const lowerFileName = fileName.toLowerCase();
  return ALLOWED_EXTENSIONS.find((ext) => lowerFileName.endsWith(ext));
};

const PhotoUpload: React.FC<PhotoUploadProps> = ({
    restaurantId,
    authToken,
    onUploadComplete,
    isModalOpen,
    setIsModalOpen
}) => {
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, UploadProgress>>({});
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Validate file type and size
  const validateFile = (file: File): string[] => {
    const errors: string[] = [];
    const extension = getFileExtension(file.name);
    const mimeType = file.type.toLowerCase();
    
    if (!extension) {
      errors.push('unsupported_type');
    } else {
      const allowedMimesForExtension = EXTENSION_TO_MIME_TYPES[extension];
      // Some browsers can provide an empty MIME type for local files.
      if (mimeType && !allowedMimesForExtension.includes(mimeType)) {
        errors.push('unsupported_type');
      }
    }

    if (
      extension &&
      mimeType === 'application/octet-stream' &&
      !['.heic', '.heif'].includes(extension)
    ) {
      errors.push('unsupported_type');
    }
    
    if (file.size > MAX_FILE_SIZE) {
      errors.push('file_too_large');
    }
    
    return errors;
  };

  const createPreviewUrl = async (file: File): Promise<string> => {
    const extension = getFileExtension(file.name);

    if (!extension || !HEIC_HEIF_EXTENSIONS.includes(extension)) {
      return URL.createObjectURL(file);
    }

    try {
      // heic2any does not ship typings in this project setup.
      const { default: heic2any } = await import('heic2any');
      const converted = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.9
      });

      const convertedBlob = Array.isArray(converted) ? converted[0] : converted;
      if (convertedBlob instanceof Blob) {
        return URL.createObjectURL(convertedBlob);
      }
    } catch (error) {
      // Fall back to the raw object URL if conversion fails.
      console.warn('Failed to convert HEIC/HEIF preview:', error);
    }

    return URL.createObjectURL(file);
  };

  // Handle file selection
  const handleFileSelect = useCallback(async (files: FileList): Promise<void> => {
    const fileArray: File[] = Array.from(files);
    const validFiles: SelectedFile[] = [];
    const invalidFiles: { file: File; errors: string[] }[] = [];

    for (const file of fileArray) {
      const errors = validateFile(file);
      if (errors.length === 0) {
        validFiles.push({
          id: `${Date.now()}-${Math.random()}`,
          file,
          caption: '',
          category: '',
          preview: '',
          isPreviewLoading: true,
          errors: []
        });
      } else {
        invalidFiles.push({ file, errors });
      }
    }

    if (invalidFiles.length > 0) {
      invalidFiles.forEach(({ file, errors }) => {
        if (errors.includes('unsupported_type')) {
          alert(`"${file.name}" is not a supported format. Please upload JPG, JPEG, PNG, WEBP, HEIC, or HEIF files.`);
        } else if (errors.includes('file_too_large')) {
          alert(`"${file.name}" is too large. Image size must be under 10MB.`);
        }
      });
    }

    if (validFiles.length === 0) {
      return;
    }

    // Show selected files immediately and load previews asynchronously.
    setSelectedFiles((prev) => [...prev, ...validFiles]);

    const previewResults = await Promise.all(
      validFiles.map(async (item) => ({
        id: item.id,
        preview: await createPreviewUrl(item.file)
      }))
    );

    setSelectedFiles((prev) => {
      let nextFiles = prev;

      previewResults.forEach(({ id, preview }) => {
        let found = false;
        nextFiles = nextFiles.map((fileData) => {
          if (fileData.id !== id) return fileData;
          found = true;

          if (fileData.preview.startsWith('blob:') && fileData.preview !== preview) {
            URL.revokeObjectURL(fileData.preview);
          }

          return {
            ...fileData,
            preview,
            isPreviewLoading: false
          };
        });

        if (!found && preview.startsWith('blob:')) {
          URL.revokeObjectURL(preview);
        }
      });

      return nextFiles;
    });
  }, []);

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      void handleFileSelect(files);
    }
  };

  // File input change handler
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files && e.target.files.length > 0) {
      void handleFileSelect(e.target.files);
    }
  };

  // Update file metadata
  const updateFileData = (fileId: string, field: keyof SelectedFile, value: string): void => {
    setSelectedFiles((prev) =>
      prev.map((f) => (f.id === fileId ? { ...f, [field]: value } : f))
    );
  };

  // Remove file from selection
  const removeFile = (fileId: string): void => {
    setSelectedFiles((prev) => {
      const updated = prev.filter((f) => f.id !== fileId);
      const removed = prev.find((f) => f.id === fileId);
      if (removed?.preview.startsWith('blob:')) {
        URL.revokeObjectURL(removed.preview);
      }
      return updated;
    });
  };

  // Upload individual file
    const uploadFile = async (fileData: SelectedFile): Promise<UploadResult> => {
    try {
        const result = await uploadToRestaurant(restaurantId, fileData.file, authToken);
        return {
        success: true,
        fileId: fileData.id,
        fileName: fileData.file.name,
        data: result,  // already JSON
        };
    } catch (error) {
        return {
        success: false,
        fileId: fileData.id,
        fileName: fileData.file.name,
        error: error instanceof Error ? error.message : 'Unknown error',
        };
    }
    };

  // Handle upload of all files
  const handleUpload = async (): Promise<void> => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    setUploadResults([]);
    const results: UploadResult[] = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      const fileData = selectedFiles[i];
      
      setUploadProgress((prev) => ({
        ...prev,
        [fileData.id]: {
          status: 'uploading',
          current: i + 1,
          total: selectedFiles.length
        }
      }));

      const result = await uploadFile(fileData);
      results.push(result);

      setUploadProgress((prev) => ({
        ...prev,
        [fileData.id]: {
          status: result.success ? 'success' : 'error',
          current: i + 1,
          total: selectedFiles.length
        }
      }));
    }

    setUploadResults(results);
    setIsUploading(false);

    const allSuccess = results.every((r) => r.success);
    if (allSuccess) {
      setTimeout(() => {
        handleCloseModal();
        if (onUploadComplete) {
          onUploadComplete(results);
        }
      }, 2000);
    }
  };

  // Retry failed upload
  const retryUpload = async (fileId: string): Promise<void> => {
    const fileData = selectedFiles.find((f) => f.id === fileId);
    if (!fileData) return;

    setUploadProgress((prev) => ({
      ...prev,
      [fileId]: { status: 'uploading' }
    }));

    const result = await uploadFile(fileData);
    
    setUploadProgress((prev) => ({
      ...prev,
      [fileId]: { status: result.success ? 'success' : 'error' }
    }));

    setUploadResults((prev) =>
      prev.map((r) => (r.fileId === fileId ? result : r))
    );
  };

  // Open/close modal
  const handleOpenModal = (): void => {
    setIsModalOpen(true);
  };

  const handleCloseModal = (): void => {
    selectedFiles.forEach((f) => {
      if (f.preview.startsWith('blob:')) {
        URL.revokeObjectURL(f.preview);
      }
    });
    setSelectedFiles([]);
    setUploadProgress({});
    setUploadResults([]);
    setIsUploading(false);
    setIsModalOpen(false);
  };

  // Handle escape key
  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape' && !isUploading) {
      handleCloseModal();
    }
  };

  return (
    <>
      {/* Upload Modal */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-5 animate-fadeIn"
          onClick={(e) => e.target === e.currentTarget && !isUploading && handleCloseModal()}
          onKeyDown={handleKeyDown}
          role="dialog"
          aria-modal="true"
          aria-labelledby="upload-modal-title"
        >
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl animate-slideUp">
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-5 border-b border-gray-200">
              <h2 id="upload-modal-title" className="text-2xl font-bold text-gray-900">
                Share your dining adventures!
              </h2>
              <button 
                className="w-10 h-10 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-green-500"
                onClick={handleCloseModal}
                disabled={isUploading}
                aria-label="Close upload dialog"
              >
                <span className="text-3xl leading-none">×</span>
              </button>
            </div>

            {/* Info Text */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Supported formats: JPG, JPEG, PNG, WEBP, HEIC, HEIF.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed">
                You may give each photo a caption and choose a category: food, ambiance, menu, exterior, or other. 
                All uploads start as pending and will appear publicly once approved.
              </p>
            </div>

            {/* Upload Area */}
            {selectedFiles.length === 0 && !isUploading && (
              <div 
                className={`mx-6 my-6 px-6 py-12 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                  isDragging 
                    ? 'border-green-600 bg-green-50 border-solid' 
                    : 'border-gray-300 bg-gray-50 hover:border-green-600 hover:bg-green-50'
                }`}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
              >
                <div className="flex flex-col items-center gap-3 text-center">
                  <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="17 8 12 3 7 8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="12" y1="3" x2="12" y2="15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <p className="text-base font-semibold text-gray-900">
                    Drag photos here or click to browse
                  </p>
                  <p className="text-sm text-gray-600">
                    Select one or multiple images
                  </p>
                </div>
              </div>
            )}
            <input 
              ref={fileInputRef}
              type="file"
              multiple
              accept={ALLOWED_EXTENSIONS.join(',')}
              onChange={handleFileInputChange}
              className="hidden"
              aria-label="Select photos to upload"
            />

            {/* File Previews */}
            {selectedFiles.length > 0 && (
              <div className="px-6 py-6 max-h-[500px] overflow-y-auto space-y-4">
                {selectedFiles.map((fileData) => (
                  <div 
                    key={fileData.id} 
                    className="flex gap-4 p-4 border border-gray-200 rounded-lg bg-white hover:shadow-md transition-shadow"
                  >
                    <div className="relative flex-shrink-0">
                      {fileData.isPreviewLoading || !fileData.preview ? (
                        <div className="w-30 h-30 rounded-md bg-gray-100 border border-gray-200 flex flex-col items-center justify-center gap-2 text-gray-500">
                          <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                          <span className="text-[10px] font-semibold">Preparing...</span>
                        </div>
                      ) : (
                        <img 
                          src={fileData.preview} 
                          alt={`Preview of ${fileData.file.name}`}
                          className="w-30 h-30 object-cover rounded-md bg-gray-100"
                        />
                      )}
                      {!isUploading && (
                        <button 
                          className="absolute z-5 -top-2 -right-2 w-7 h-7 rounded-full bg-red-500 text-white border-2 border-white flex items-center justify-center hover:bg-red-600 hover:scale-110 transition-all focus:outline-none focus:ring-2 focus:ring-red-500"
                          onClick={() => removeFile(fileData.id)}
                          aria-label={`Remove ${fileData.file.name}`}
                        >
                          <span className="text-lg leading-none">×</span>
                        </button>
                      )}
                      
                      {/* Upload Status Overlay */}
                      {uploadProgress[fileData.id] && (
                        <div className="absolute inset-0 bg-black/50 rounded-md flex items-center justify-center">
                          {uploadProgress[fileData.id].status === 'uploading' && (
                            <div className="flex flex-col items-center gap-2 text-white">
                              <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                              <span className="text-xs font-semibold">Uploading...</span>
                            </div>
                          )}
                          {uploadProgress[fileData.id].status === 'success' && (
                            <div className="text-green-500">
                              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <polyline points="20 6 9 17 4 12" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                          )}
                          {uploadProgress[fileData.id].status === 'error' && (
                            <div className="text-red-500">
                              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                                <line x1="15" y1="9" x2="9" y2="15" strokeWidth="2"/>
                                <line x1="9" y1="9" x2="15" y2="15" strokeWidth="2"/>
                              </svg>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0 flex flex-col gap-3">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {fileData.file.name}
                      </p>
                      
                      {/* Caption Input */}
                      <input 
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                        placeholder="Add a caption..."
                        value={fileData.caption}
                        onChange={(e) => updateFileData(fileData.id, 'caption', e.target.value)}
                        disabled={isUploading}
                        aria-label={`Caption for ${fileData.file.name}`}
                      />
                      
                      {/* Category Select */}
                      <select 
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                        value={fileData.category}
                        onChange={(e) => updateFileData(fileData.id, 'category', e.target.value)}
                        disabled={isUploading}
                        aria-label={`Category for ${fileData.file.name}`}
                      >
                        {CATEGORIES.map((cat) => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label}
                          </option>
                        ))}
                      </select>

                      {/* Upload Result Messages */}
                      {uploadProgress[fileData.id]?.status === 'success' && (
                        <p className="text-xs font-semibold text-green-600">
                          ✓ Uploaded successfully
                        </p>
                      )}
                      {uploadProgress[fileData.id]?.status === 'error' && (
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-semibold text-red-600">
                            ❌ Upload failed
                          </p>
                          <button 
                            className="px-3 py-1 bg-red-500 text-white text-xs font-semibold rounded hover:bg-red-600 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                            onClick={() => retryUpload(fileData.id)}
                          >
                            Retry
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Add More Files Button */}
                {!isUploading && (
                  <button 
                    className="w-full py-3 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg text-green-600 text-sm font-semibold hover:bg-green-50 hover:border-green-600 transition-colors focus:outline-none focus:ring-2 focus:ring-[#FFF8E7]"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    + Add More Photos
                  </button>
                )}
              </div>
            )}

            {/* Progress Summary */}
            {isUploading && (
              <div 
                className="px-6 py-4 bg-green-50 border-y border-green-200 text-center" 
                role="status" 
                aria-live="polite"
              >
                <p className="text-sm font-semibold text-gray-900">
                  Uploading... ({Object.values(uploadProgress).filter((p) => p.status === 'success').length} of {selectedFiles.length} photos uploaded)
                </p>
              </div>
            )}

            {/* Success Message */}
            {uploadResults.length > 0 && uploadResults.every((r) => r.success) && (
              <div 
                className="mx-6 my-6 p-5 bg-green-50 border border-green-600 rounded-lg flex gap-4 items-start animate-slideDown" 
                role="alert"
              >
                <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <polyline points="20 6 9 17 4 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div>
                  <p className="text-base font-bold text-gray-900 mb-1">
                    Upload successful!
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Your photos are now pending approval and will appear in your profile shortly.
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 justify-end px-6 py-5 border-t border-gray-200">
              {!isUploading && uploadResults.length === 0 && (
                <>
                  <button 
                    className="px-5 py-2.5 bg-white text-gray-700 border border-gray-300 font-semibold text-sm rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500"
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </button>
                  <button 
                    className="px-5 py-2.5 bg-[#ee5210] border border-gray-600 text-gray-700 font-semibold text-white text-sm rounded-md hover:opacity-90 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed min-w-[140px] focus:outline-none focus:ring-2 focus:ring-[#FFF8E7]"
                    onClick={handleUpload}
                    disabled={selectedFiles.length === 0}
                  >
                    Upload Photos ({selectedFiles.length})
                  </button>
                </>
              )}
              
              {uploadResults.length > 0 && !uploadResults.every((r) => r.success) && (
                <button 
                  className="px-6 py-2.5 bg-green-600 text-white font-semibold text-sm rounded-md hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500"
                  onClick={handleCloseModal}
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes slideDown {
          from {
            transform: translateY(-10px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease;
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease;
        }
      `}</style>
    </>
  );
};

export default PhotoUpload;